import { RefObject, useEffect, useRef } from 'react'

type Input = {
  threshold?: number
  ref: RefObject<HTMLElement>
  vertical?: boolean
  horizontal?: boolean
  onSlideRight?: VoidFunction
  onSlideLeft?: VoidFunction
  onSlideUp?: VoidFunction
  onSlideDown?: VoidFunction
}

export const useSlideGesture = ({
  threshold = 50,
  ref,
  vertical = false,
  horizontal = false,
  onSlideRight = () => undefined,
  onSlideLeft = () => undefined,
  onSlideUp = () => undefined,
  onSlideDown = () => undefined,
}: Input) => {
  const startX = useRef<number | null>(null)
  const startY = useRef<number | null>(null)

  if (!vertical && !horizontal) {
    throw new Error(
      'You need to set at least one of vertical or horizontal to true',
    )
  }

  useEffect(() => {
    const handleTouchStart = (e: TouchEvent) => {
      if (horizontal) {
        startX.current = e.touches[0].clientX
      }
      if (vertical) {
        startY.current = e.touches[0].clientY
      }
    }

    const handleTouchEnd = (e: TouchEvent) => {
      if (startX.current !== null && horizontal) {
        const endX = e.changedTouches[0].clientX
        const diffX = endX - startX.current

        if (diffX > threshold) {
          onSlideRight()
        }
        else if (diffX < -threshold) {
          onSlideLeft()
        }
        startX.current = null
      }

      if (startY.current !== null && vertical) {
        const endY = e.changedTouches[0].clientY
        const diffY = endY - startY.current

        if (diffY > threshold) {
          onSlideUp()
        }
        else if (diffY < -threshold) {
          onSlideDown()
        }
        startY.current = null
      }
    }

    if (!ref.current) return undefined

    const node = ref.current
    if (node) {
      node.addEventListener('touchstart', handleTouchStart)
      node.addEventListener('touchend', handleTouchEnd)
    }
    return () => {
      if (node) {
        node.removeEventListener('touchstart', handleTouchStart)
        node.removeEventListener('touchend', handleTouchEnd)
      }
    }
  }, [
    horizontal,
    onSlideDown,
    onSlideLeft,
    onSlideRight,
    onSlideUp,
    ref,
    threshold,
    vertical,
  ])
}
